import React, { useState ,useEffect ,useMemo} from "react";

import { Box, Typography, useTheme, Paper, Dialog, DialogContent } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "../../mech/casting/ProgressiveAnalysisTracker";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { LaminatesQuestions } from "./questions";
import { LaminatesPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { apiFetch } from "../../../api/apiClient";

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set([
  "currency",
  "total_should_cost",
  "total",
  "logistics",
  "packaging",
  "consumables",
]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  // packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};
 
const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
 
const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });
 
  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
 
  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

export default function LaminatesPage() {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [keyInsightsData, setKeyInsightsData] = useState(null);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
  };

  // Helper function to recalculate OHP cost when OHP Rate changes
  const recalculateOHPCost = (newOhpRate, costData, updatedCostParams) => {
    if (!costData || !costData.breakdown || !costData._originalResult) {
      return costData;
    }

    const to4 = (n) => {
      if (n === null || n === undefined || n === '') return 0;
      if (typeof n === 'string') {
        const cleaned = n.replace(/[$,\s]/g, '').trim();
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
      }
      return Number(Number(n).toFixed(4));
    };

    // Get base material and process costs (these don't change)
    const material = Number(costData.breakdown.material || 0);
    const process = Number(costData.breakdown.process || 0);
    const baseOhpCost = Number(costData.meta?.baseOhpCost ?? costData.breakdown.ohp ?? 0);
    const baseOhpRate =
      Number(costData.meta?.baseOhpRate) ||
      (material + process > 0 ? (baseOhpCost / (material + process)) * 100 : 0);
    const baseFormulaCalc =
      material + process > 0 ? (material + process) * (baseOhpRate / 100) : 0;
    console.log(
      "🧮 Base OHP Formula:",
      `(${material} + ${process}) × (${baseOhpRate}/100) = ${baseFormulaCalc}`
    );

    // Recalculate OHP based on percentage change relative to original rate
    const targetOhpRate =
      typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate ?? 0);
    console.log(
      "➡️ Recalculating OHP:",
      `Current meta rate=${costData.meta?.currentOhpRate}, base rate=${baseOhpRate}`
    );

    let ohp = baseOhpCost;
    if (!isNaN(targetOhpRate)) {
      if (baseOhpRate > 0) {
        const rateRatio = targetOhpRate / baseOhpRate;
        const calc = baseOhpCost * rateRatio;
        console.log(
          "📐 OHP (based on ratio):",
          `(${baseOhpCost} * ${rateRatio}) = ${calc}`
        );
        ohp = to4(calc);
        } else {
        const ohpRateDecimal =
          targetOhpRate > 1 ? targetOhpRate / 100 : targetOhpRate;
        const calc = (material + process) * ohpRateDecimal;
        console.log(
          "📐 OHP (Material+Process * rate):",
          `(${material} + ${process}) * ${ohpRateDecimal} = ${calc}`
        );
        ohp = to4(calc);
      }
      const targetFormulaCalc =
        (material + process) * (targetOhpRate / 100);
      console.log(
        "🧮 Target OHP Formula:",
        `(${material} + ${process}) × (${targetOhpRate}/100) = ${targetFormulaCalc}`
      );
    }

    // Calculate new total: material + process + ohp
    const calculatedTotal = Number(material) + Number(process) + Number(ohp);
    const total = to4(calculatedTotal);

    // Update assumptions array with updated PlayBook values
    const resultData = costData._originalResult;
    const assumptionsArray = [];
    
    // Part Dimensions - from product data
    const dimensionData = productData?.partWeightDimensions || {};
    if (dimensionData.partLength && dimensionData.partWidth && dimensionData.finishWeight) {
      const heightPart = dimensionData.partHeight ? `, H: ${dimensionData.partHeight}mm` : "";
      assumptionsArray.push(
        `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm${heightPart}, Weight: ${dimensionData.finishWeight}g`
      );
    }
    
    // Machine Tonnage
    if (updatedCostParams.machineTonnage) {
      assumptionsArray.push(`Machine tonnage: ${updatedCostParams.machineTonnage}`);
    } else if (resultData.Tonnage || resultData.cTonnage) {
      assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
    }
    
    // Cavities
    if (updatedCostParams.cavities) {
      assumptionsArray.push(`Cavities: ${updatedCostParams.cavities}`);
    } else if (resultData.cCavities || resultData.Cavities) {
      assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
    }
    
    // Cycle Time
    if (updatedCostParams.cycleTime) {
      assumptionsArray.push(`Cycle time: ${updatedCostParams.cycleTime}s`);
    } else if (resultData.CycleTime) {
      assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
    }
    
    // Machine Rate - always from stored procedure
    if (resultData.MHR) {
      const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
      if (!isNaN(machineRate)) {
        assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
      }
    }
    
    // Material Price - always from stored procedure
    if (resultData.RMPrice) {
      const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
      if (!isNaN(materialPrice)) {
        assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
      }
    }
    
    // Labour Cost - always from stored procedure
    if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
      const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
        ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
        : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
      if (!isNaN(labourCost)) {
        assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
      }
    }
    
    // OHP Rate - use updated value
    if (newOhpRate !== null && newOhpRate !== undefined && newOhpRate !== 0) {
      const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
      if (!isNaN(ohpRateValue)) {
        assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
      }
    }

    // Update cost data with new OHP, total, and assumptions
    return {
      ...costData,
      breakdown: {
        ...costData.breakdown,
        ohp: ohp,
        total_should_cost: total,
      },
      summary: costData.summary.replace(
        /\$[\d.]+ per unit/,
        `$${total} per unit`
      ),
      assumptions:
        assumptionsArray.length > 0 ? assumptionsArray : costData.assumptions,
      meta: {
        ...costData.meta,
        currentOhpRate: targetOhpRate,
      },
    };
  };

  // Helper function to fetch material price and update material cost when tonnage changes
  const updateMaterialCostFromTonnage = async (tonnage, updatedCostParams) => {
    console.log("\n" + "=".repeat(80));
    console.log("💰 updateMaterialCostFromTonnage called");
    console.log("=".repeat(80));
    console.log("📥 Input Parameters:");
    console.log("   - Tonnage:", tonnage);
    console.log("   - CostData exists:", !!costData);
    console.log("   - CostData source:", costData?._source);
    console.log("   - ProductData exists:", !!productData);

    if (!costData || costData._source !== "stored_procedure" || !productData) {
      console.log("⚠️ Early return: Missing costData or productData");
      return;
    }

    try {
      const manufacturingCountry = productData.manufacturingCountry || "";
      const materialName = productData.materialSelection || null;
      const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

      console.log("📋 Product Data:");
      console.log("   - Manufacturing Country:", manufacturingCountry);
      console.log("   - Material Name:", materialName);
      console.log("   - Part Weight (g):", partWeightG);

      if (!manufacturingCountry || !tonnage || partWeightG <= 0) {
        console.warn("⚠️ Cannot update material cost: missing country, tonnage, or part weight");
        console.log("   - Country:", manufacturingCountry || "MISSING");
        console.log("   - Tonnage:", tonnage || "MISSING");
        console.log("   - Part Weight:", partWeightG || "MISSING");
        return;
      }

      // Get current material cost before update
      const currentMaterialCost = costData.breakdown?.material || 0;
      const currentMaterialPricePerKg = costData.meta?.materialPricePerKg || null;
      
      console.log("\n📊 Current Values:");
      console.log("   - Current Material Cost:", `$${currentMaterialCost}/unit`);
      console.log("   - Current Material Price:", currentMaterialPricePerKg ? `$${currentMaterialPricePerKg}/kg` : "N/A");

      const requestPayload = {
        tonnage: tonnage,
        manufacturingCountry: manufacturingCountry,
        materialName: materialName,
      };

      console.log("\n📤 API Request Payload:");
      console.log(JSON.stringify(requestPayload, null, 2));
      console.log(`\n🔁 Fetching material price for ${tonnage} tons in ${manufacturingCountry}...`);

      const response = await apiFetch("/api/mech/material-price", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      });

      console.log("📥 API Response Status:", response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
        console.error("❌ API Error Response:", errorData);
        throw new Error(errorData.error || "Failed to fetch material price");
      }

      const result = await response.json();
      console.log("\n📥 API Response Data:");
      console.log(JSON.stringify(result, null, 2));

      const newMaterialPricePerKg = parseFloat(result.material_price || 0);
      console.log("\n💰 Material Price Parsed:");
      console.log("   - Raw value:", result.material_price);
      console.log("   - Parsed value:", newMaterialPricePerKg);

      if (isNaN(newMaterialPricePerKg) || newMaterialPricePerKg <= 0) {
        console.warn("⚠️ Invalid material price received:", result);
        return;
      }

      // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
      // const partWeightKg = parseFloat(partWeightG) / 1000;
      // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
      // Update Material in cost category table with material price per kg directly
      const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

      console.log("\n🧮 Calculation:");
      console.log("   - Material Price ($/kg):", newMaterialPricePerKg);
      console.log("   - Using material price per kg directly as material cost");
      console.log("   - New Material Cost ($/unit):", newMaterialCost);

      // Update costData with new material cost
      const to4 = (n) => {
        if (n === null || n === undefined || n === '') return 0;
        if (typeof n === 'string') {
          const cleaned = n.replace(/[$,\s]/g, '').trim();
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
        }
        return Number(Number(n).toFixed(4));
      };

      const process = to4(costData.breakdown.process || 0);
      const ohp = to4(costData.breakdown.ohp || 0);
      const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

      console.log("\n📊 Cost Breakdown Update:");
      console.log("   - Material (old):", `$${currentMaterialCost}/unit`);
      console.log("   - Material (new):", `$${newMaterialCost}/unit`);
      console.log("   - Process:", `$${process}/unit`);
      console.log("   - OHP:", `$${ohp}/unit`);
      console.log("   - Total (old):", `$${costData.breakdown.total_should_cost}/unit`);
      console.log("   - Total (new):", `$${newTotal}/unit`);
      console.log("   - Total Formula:", `${newMaterialCost} + ${process} + ${ohp} = ${newTotal}`);

      const updatedCostData = {
        ...costData,
        breakdown: {
          ...costData.breakdown,
          material: newMaterialCost,
          total_should_cost: newTotal,
        },
        summary: costData.summary.replace(
          /\$[\d.]+ per unit/,
          `$${newTotal} per unit`
        ),
        assumptions: costData.assumptions.map((assumption) => {
          // Update material price in assumptions if it exists
          if (assumption.includes("Material price:")) {
            return `Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`;
          }
          return assumption;
        }),
        meta: {
          ...costData.meta,
          materialPricePerKg: newMaterialPricePerKg,
        },
      };

      setCostData(updatedCostData);
      console.log("\n✅ Material cost updated successfully!");
      console.log("   - New Material Price:", `$${newMaterialPricePerKg}/kg`);
      console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);
      console.log("   - New Total Cost:", `$${newTotal}/unit`);
      console.log("=".repeat(80) + "\n");
    } catch (error) {
      console.error("\n❌ Failed to update material cost:");
      console.error("   - Error type:", error.constructor.name);
      console.error("   - Error message:", error.message);
      console.error("   - Stack:", error.stack);
      console.log("=".repeat(80) + "\n");
      // Don't show alert to user, just log the error
    }
  };

  const handlePlayBookChange = (field, value) => {
    // If updating costParams, merge it properly
    if (field === "costParams") {
      const previousOhpRate = playBookData?.costParams?.ohpRate;
      const newOhpRate = value.ohpRate;
      const previousTonnage = playBookData?.costParams?.machineTonnage;
      const newTonnage = value.machineTonnage;
      
      setPlayBookData({
        ...playBookData,
        costParams: value,
      });
      setKeyInsightsData(null);

      // If OHP Rate changed and we have existing costData, recalculate OHP cost
      if (costData && costData._source === "stored_procedure" && 
          newOhpRate !== undefined && newOhpRate !== null && 
          newOhpRate !== previousOhpRate) {
        const updatedCostData = recalculateOHPCost(newOhpRate, costData, value);
        setCostData(updatedCostData);
      }

      // For corrugated: Update OHP rate in assumptions when PlayBook changes
      if (costData && costData._source !== "stored_procedure" && 
          newOhpRate !== undefined && newOhpRate !== null && 
          newOhpRate !== previousOhpRate) {
        const updatedAssumptions = (costData.assumptions || []).map((assumption) => {
          // Replace "OHP: X%" or "OHP rate: X%" with the new value
          if (typeof assumption === 'string') {
            const lowerAssumption = assumption.toLowerCase();
            if (lowerAssumption.startsWith("ohp:") || lowerAssumption.startsWith("ohp rate:")) {
              const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
              if (!isNaN(ohpRateValue)) {
                return `OHP: ${ohpRateValue.toFixed(2)}%`;
              }
            }
          }
          return assumption;
        });
        
        // If OHP wasn't found in assumptions, add it
        const hasOhp = updatedAssumptions.some((a) => 
          typeof a === 'string' && (a.toLowerCase().startsWith("ohp:") || a.toLowerCase().startsWith("ohp rate:"))
        );
        if (!hasOhp && newOhpRate !== undefined && newOhpRate !== null && newOhpRate !== 0) {
          const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
          if (!isNaN(ohpRateValue)) {
            updatedAssumptions.push(`OHP: ${ohpRateValue.toFixed(2)}%`);
          }
        }

        setCostData({
          ...costData,
          assumptions: updatedAssumptions,
        });
      }

      // Note: Material price update is now handled when Quick Cost button is clicked
      // No automatic update here - wait for user to click Quick Cost button
    } else {
    setPlayBookData({ ...playBookData, [field]: value });
    }
  };

  // Helper function to analyze cost with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      // Call all three APIs in parallel
      const [costResponse, riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/fnb/analyze-cost", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/analyze-risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/find-substitutes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Check responses
      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      // Parse all responses (non-blocking for risk and substitute)
      const costResult = await costResponse.json();
      console.log("✅ Cost analysis complete:", costResult);
      setCostData(costResult);

      // Handle risk analysis
      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        console.log("✅ Risk analysis complete:", riskResult);
        setRiskData(riskResult);
      } else {
        console.warn("⚠️ Risk analysis failed, continuing without risk data");
        setRiskData(null);
      }

      // Handle substitute analysis
      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        console.log("✅ Substitute analysis complete:", substituteResult);
        setSubstituteData(substituteResult);
      } else {
        console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
        setSubstituteData(null);
      }

      // Generate product image with the playbook data
      await generateProductImageWithData(product, playbook);

      // Navigate to results section and mark as started
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithData(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const fetchRiskAndSubstituteData = async (product, playbook, costBreakdownOverride = null) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Packaging",
      costModel: costBreakdownOverride || costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [{ ingredient: product?.materialSelection || "Laminates", percentage: 100, functional_group: "Base" }];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "laminate packaging"} based on available costing inputs.`,
      top_concerns: [
        "Film and resin price volatility can shift laminate unit cost quickly.",
        "Yield loss from wastage, setup, or sealing variation can erode margins.",
        "Supplier lead-time or structure changes can affect continuity and qualification.",
      ],
      risk_items: [
        { title: "Material Cost Volatility", severity: 6, probability: 6, detail: "Track resin, foil, and specialty film price movement across approved suppliers." },
        { title: "Conversion and Yield Variability", severity: 5, probability: 5, detail: "Monitor wastage, process efficiency, and seal-quality related scrap." },
      ],
      assumptions: "Generated from laminates product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const dims = product?.partWeightDimensions || {};
      const details = product?.partDetails || {};
      const layers = Array.isArray(details.layers) ? details.layers : [];
      const layerSummary = layers.length > 0
        ? layers.map((layer) => `${layer.layerName || "Layer"} ${layer.filmStructure || ""}`.trim()).join(", ")
        : "Current laminate structure";

      return {
        summary: `Laminate optimization recommendations generated for ${product?.productName || "the current packaging"} using provided inputs.`,
        substitutes: [
          {
            current_item: `Structure: ${layerSummary}`,
            proposed_item: "Validate downgauged or simplified layer structure",
            estimated_cost_delta_per_unit: "Potential material cost reduction",
            impact_on_quality: "Requires barrier and seal validation",
            feasibility_score: 6,
            timeline_weeks: 4,
            risk_note: "Confirm shelf-life, machinability, and sealing performance.",
            validation_steps: ["Structure review", "Barrier validation", "Line trial"],
          },
          {
            current_item: `Format: ${dims.measurementBasis || "repeat"} basis laminate`,
            proposed_item: "Optimize repeat, coil width, and wastage settings",
            estimated_cost_delta_per_unit: "Potential conversion and yield savings",
            impact_on_quality: "Low to medium",
            feasibility_score: 7,
            timeline_weeks: 3,
            risk_note: "Verify print layout, pack fit, and scrap impact before release.",
            validation_steps: ["Layout review", "Trial run", "QA approval"],
          },
        ],
        prioritization: [
          "Yield and wastage optimization for near-term savings",
          "Layer structure simplification after validation",
        ],
        assumptions: "Generated from laminates product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!riskResponse.ok || !substituteResponse.ok) {
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        setRiskData(await riskResponse.json());
      } else {
        setRiskData(buildFallbackRisk());
      }

      if (substituteResponse.ok) {
        setSubstituteData(await substituteResponse.json());
      } else {
        setSubstituteData(buildFallbackSubstitute());
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(buildFallbackRisk());
      setSubstituteData(buildFallbackSubstitute());
    }
  };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    setGeneratingImage(true);

    try {
      const dims = product.partWeightDimensions || {};
      const partDetails = product.partDetails || {};
      const layers = Array.isArray(partDetails.layers) ? partDetails.layers : [];

      const imagePayload = {
        productName: product.productName,
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: "Laminates",
        primaryPackaging: playbook?.primary_packaging || "",
        packagingType: playbook?.primary_packaging || "",
        skuWt: product.skuWeight || "",
        skuWtUnit: product.skuWeightUnit || "g",
        marketPositioning: product.marketPositioning || "",
        manufacturingCountry: product.manufacturingCountry || "",
        measurementBasis: dims.measurementBasis || "repeat",
        partWeightDimensions: dims,
        partDetails: {
          ...partDetails,
          layers,
        },
        layers,
        style: "professional",
      };

      console.log("🖼️ Generating product image:", imagePayload);

      const response = await apiFetch("/api/pkg/laminates/generate-product-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      setProductImageUrl(data.image_url);
      console.log("✅ Product image generated:", data.image_url);
    } catch (error) {
      console.error("❌ Error generating product image:", error);
      // Don't alert - image is optional
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  const fetchKeyInsights = async (product, playbook) => {
    if (!product) {
      return;
    }
    try {
      const response = await apiFetch("/api/mech/key-insights", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData: product,
          playBookData: playbook || {},
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate key insights");
      }
      const insights = await response.json();
      setKeyInsightsData(insights);
    } catch (error) {
      console.error("Failed to fetch key insights:", error);
    }
  };

  // Call corrugated costing calculation
  const callCorrugatedCosting = async (productData, playBookData) => {
    try {
      console.log("📞 Calling corrugated costing API with:", { productData, playBookData });

      // Prepare payload - convert dimensions from mm to cm
      const dimensionData = productData.partWeightDimensions || {};
      const detailsData = productData.partDetails || {};
      
      // Convert dimensions from mm to cm for the calculation
      const lengthCm = dimensionData.partLength ? parseFloat(dimensionData.partLength) / 10 : 0;
      const widthCm = dimensionData.partWidth ? parseFloat(dimensionData.partWidth) / 10 : 0;
      const heightCm = dimensionData.partHeight ? parseFloat(dimensionData.partHeight) / 10 : 0;

      const payload = {
        productData: {
          ...productData,
          partWeightDimensions: {
            ...dimensionData,
            length_cm: lengthCm,
            width_cm: widthCm,
            height_cm: heightCm,
          },
          partDetails: detailsData,
        },
        playBookData: playBookData || {},
      };

      console.log("📤 Payload being sent:", payload);

      // Call the corrugated costing API endpoint
      const response = await apiFetch("/api/costing/corrugated", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Read response as text first to handle both success and error cases
      const responseText = await response.text();
      
      if (!response.ok) {
        // Try to parse error response as JSON
        let errorMessage = `Corrugated costing call failed (${response.status})`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (jsonErr) {
          // If JSON parsing fails, use the text as error message
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Parse successful response as JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (jsonErr) {
        console.error("Failed to parse response as JSON:", jsonErr);
        console.error("Response text:", responseText);
        throw new Error("Invalid JSON response from server");
      }
      
      console.log("✅ Corrugated costing result:", result);
      return result;
    } catch (err) {
      console.error("❌ Corrugated costing call failed:", err);
      throw err;
    }
  };

  // New Laminates Quick Cost handler
  const callLaminatesCosting = async (productData) => {
    try {
      console.log("📞 Calling laminates costing API with:", { productData });

      // Validate and normalize layers before sending
      const layers = productData?.partDetails?.layers || [];
      if (layers.length === 0) {
        throw new Error("At least one layer is required. Please add layers in Part Details.");
      }

      // Collect validation errors for all layers
      const validationErrors = [];
      const normalizedLayers = [];

      layers.forEach((layer, index) => {
        const microns = layer.microns;
        const micronsNum = typeof microns === 'number' 
          ? microns 
          : (typeof microns === 'string' && microns.trim() !== '' ? parseFloat(microns) : 0);
        
        // Check if layer has valid microns (allow 0, but not empty, null, undefined, or NaN)
        if (microns === '' || microns === null || microns === undefined || isNaN(micronsNum) || micronsNum < 0) {
          const layerName = layer.layerName || `Layer ${index + 1}`;
          const currentValue = microns === '' || microns === null || microns === undefined 
            ? 'empty' 
            : (isNaN(micronsNum) ? `invalid (${microns})` : `negative (${micronsNum})`);
          validationErrors.push(`${layerName}: Microns value is ${currentValue}`);
        } else {
          // Layer is valid (including microns = 0), add to normalized layers
          normalizedLayers.push({
            ...layer,
            microns: micronsNum,
          });
        }
      });

      // If there are validation errors, throw a comprehensive error message
      if (validationErrors.length > 0) {
        const errorMessage = 
          `Please fix the following layer issues before running Quick Cost:\n\n` +
          validationErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n') +
          `\n\nAll layers must have a valid Microns value (0 or greater).`;
        throw new Error(errorMessage);
      }

      // If no valid layers remain after filtering, throw error
      if (normalizedLayers.length === 0) {
        throw new Error("No valid layers found. Please ensure all layers have valid Microns values (0 or greater).");
      }

      // Normalize dimensions from question area so payload always carries usable values.
      const dims = productData?.partWeightDimensions || {};
      const isPouchBasis = (dims.measurementBasis || "repeat") === "pouch";
      const normalizedDimensions = {
        ...dims,
        partLength: isPouchBasis ? (dims.pouchLength || "") : (dims.partLength || ""),
        partWidth: isPouchBasis ? (dims.pouchWidth || "") : (dims.partWidth || ""),
      };

      // Create normalized product data with validated layers
      const normalizedProductData = {
        ...productData,
        partWeightDimensions: normalizedDimensions,
        partDetails: {
          ...productData.partDetails,
          layers: normalizedLayers,
        },
      };

      console.log("✅ Normalized layers:", normalizedLayers);

      const payload = {
        productData: normalizedProductData,
      };

      const response = await apiFetch("/api/costing/laminates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = `Laminates costing call failed (${response.status} ${response.statusText})`;
        try {
          if (responseText && responseText.trim()) {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorData.message || errorData.error_message || errorMessage;
            if (errorData.error_message && errorData.error_message !== errorMessage) {
              errorMessage += ` | ${errorData.error_message}`;
            }
          }
        } catch (jsonErr) {
          // If JSON parsing fails, use the text as error message if available
          if (responseText && responseText.trim()) {
            errorMessage = responseText.length > 200 ? responseText.substring(0, 200) + '...' : responseText;
          }
        }
        console.error("❌ Laminates costing error:", {
          status: response.status,
          statusText: response.statusText,
          errorBody: responseText,
          errorMessage: errorMessage
        });
        throw new Error(errorMessage);
      }

      // Check if response has content
      if (!responseText || !responseText.trim()) {
        console.error("❌ Empty response from laminates costing API");
        throw new Error("Empty response from laminates costing API. Please try again.");
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (jsonErr) {
        console.error("❌ Failed to parse laminates response as JSON:", jsonErr);
        console.error("   Response text (first 500 chars):", responseText.substring(0, 500));
        console.error("   Response length:", responseText.length);
        throw new Error(
          `Invalid JSON response from laminates costing API. ` +
          `Response: ${responseText.substring(0, 200)}${responseText.length > 200 ? '...' : ''}`
        );
      }

      console.log("✅ Laminates costing result:", result);
      return result;
    } catch (err) {
      console.error("❌ Laminates costing call failed:", err);
      // Ensure we always throw an Error object with a message
      if (err instanceof Error) {
        throw err;
      } else {
        const errorMessage = typeof err === 'string' 
          ? err 
          : (err?.message || err?.error || JSON.stringify(err) || 'Unknown error occurred');
        throw new Error(errorMessage);
      }
    }
  };

  const runQuickCost = async () => {
    if (!productData) return;

    setLoadingMessage("Generating Quick Cost...");
    setIsProcessing(true);
    setKeyInsightsData(null);

    try {
      console.log("📞 Calling laminates costing with:", { productData });
      const costResult = await callLaminatesCosting(productData);

      if (costResult && costResult.breakdown) {
        // Map to costData shape expected by ResultsSection
        const breakdown = costResult.breakdown || {};
        const assumptions = [
          `Batch size: ${productData.batchSize || "N/A"}`,
          `Total batch cost: $${(breakdown.total_should_cost * (productData.batchSize || 1)).toFixed(4)}`,
          `Total cost per unit: $${(breakdown.total_should_cost || 0).toFixed(4)}`,
          costResult.final_summary
            ? `Total cost per tonne: $${(costResult.final_summary.total_cost_per_tonne || 0).toFixed(4)}`
            : null,
        ].filter(Boolean);

        // Extract keyInsights from costResult if available, or generate from cost data
        let keyInsights = costResult.keyInsights || costResult.key_insights || null;
        
        // If keyInsights not in response, generate basic insights from cost breakdown
        if (!keyInsights && breakdown) {
          const totalCost = breakdown.total_should_cost || 0;
          const materialCost = breakdown.material || 0;
          const processCost = breakdown.process || 0;
          const ohpCost = breakdown.ohp || 0;

          const costSavings = [];
          const strategic = [];

          // Generate cost savings insights
          if (totalCost > 0) {
            const materialPct = totalCost > 0 ? ((materialCost / totalCost) * 100).toFixed(1) : 0;
            const processPct = totalCost > 0 ? ((processCost / totalCost) * 100).toFixed(1) : 0;
            
            if (materialPct > 50) {
              costSavings.push(`Material cost represents ${materialPct}% of total cost - consider material optimization opportunities`);
            }
            if (processPct > 30) {
              costSavings.push(`Process cost represents ${processPct}% of total cost - evaluate process efficiency improvements`);
            }
            if (materialCost > 0 && processCost > 0) {
              costSavings.push(`Review layer structure and thickness to optimize material usage`);
            }
          }

          // Generate strategic insights
          if (productData.batchSize) {
            const batchSize = parseFloat(productData.batchSize) || 0;
            if (batchSize > 0 && totalCost > 0) {
              const batchTotal = totalCost * batchSize;
              strategic.push(`Total batch cost: $${batchTotal.toFixed(2)} for ${batchSize} units`);
            }
          }
          
          if (costResult.final_summary?.total_cost_per_tonne) {
            strategic.push(`Cost per tonne: $${parseFloat(costResult.final_summary.total_cost_per_tonne).toFixed(2)}`);
          }

          if (productData.manufacturingCountry) {
            strategic.push(`Manufacturing location: ${productData.manufacturingCountry} - consider regional cost variations`);
          }

          // Only create keyInsights if we have insights to show
          if (costSavings.length > 0 || strategic.length > 0) {
            keyInsights = {
              cost_savings: costSavings,
              strategic: strategic,
            };
          }
        }

        if (keyInsights) {
          setKeyInsightsData(keyInsights);
        } else {
          setKeyInsightsData(null);
        }

        const costDataNormalized = {
          summary: costResult.summary || `Laminates cost: $${(breakdown.total_should_cost || 0).toFixed(4)} per unit`,
          breakdown: breakdown,
          assumptions: assumptions,
          layer_properties: costResult.layer_properties || [],
          material_cost_summary: costResult.material_cost_summary || [],
          final_summary: costResult.final_summary || {},
          meta: costResult.meta || {},
          key_insights: keyInsights ? (keyInsights.cost_savings || []).concat(keyInsights.strategic || []) : [],
          _source: "laminates_formula",
        };

        setCostData(costDataNormalized);

        // Initialize PlayBook data from cost result and product data
        const existingCostParams = playBookData?.costParams || {};
        const isFirstRun = !playBookData || !playBookData.costParams;

        // Helper function to safely parse numeric values
        const parseNumericValue = (value, defaultValue = 0) => {
          if (value === null || value === undefined || value === "") return defaultValue;
          if (typeof value === "number") return isNaN(value) ? defaultValue : value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? defaultValue : parsed;
        };

        // Extract dimensions from product data (in mm, convert to cm for playbook)
        const partLength = parseNumericValue(
          productData?.partWeightDimensions?.partLength,
          existingCostParams.blankLengthCm ? existingCostParams.blankLengthCm * 10 : 0
        );
        const partWidth = parseNumericValue(
          productData?.partWeightDimensions?.partWidth,
          existingCostParams.blankWidthCm ? existingCostParams.blankWidthCm * 10 : 0
        );

        // Calculate area in m² (from mm²: partLength * partWidth / 1,000,000)
        const areaMm2 = partLength * partWidth;
        const areaM2 = areaMm2 / 1000000;

        // Extract OHP rate from breakdown if available, or use existing value
        const ohpCost = breakdown.ohp || 0;
        const totalCost = breakdown.total_should_cost || 0;
        const calculatedOhpRate = totalCost > 0 ? ((ohpCost / totalCost) * 100) : 0;

        // Initialize playbook data
        const playbookFromResult = {
          costParams: {
            blankLengthCm: (isFirstRun || existingCostParams.blankLengthCm === 0)
              ? partLength / 10  // Convert mm to cm
              : existingCostParams.blankLengthCm,
            blankWidthCm: (isFirstRun || existingCostParams.blankWidthCm === 0)
              ? partWidth / 10  // Convert mm to cm
              : existingCostParams.blankWidthCm,
            areaNetM2: (isFirstRun || existingCostParams.areaNetM2 === 0)
              ? areaM2
              : existingCostParams.areaNetM2,
            wastagePercent: existingCostParams.wastagePercent || 0,
            ohpRate: (isFirstRun || existingCostParams.ohpRate === 0)
              ? parseNumericValue(calculatedOhpRate, 0)
              : existingCostParams.ohpRate,
          },
        };

        console.log("🔧 [LaminatesPage] Setting playbook data:", {
          isFirstRun,
          playbookFromResult,
          existingCostParams,
          productData: {
            partLength,
            partWidth,
            areaMm2,
            areaM2,
          },
          extractedValues: {
            ohpRate: calculatedOhpRate,
          },
        });

        setPlayBookData(playbookFromResult);

        // Navigate to results section
        setActiveSection("results");
        setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));
        // Trigger image generation after Quick Cost completes (non-blocking)
        generateProductImageWithData(productData, playBookData || {});
      } else {
        throw new Error("Laminates costing returned no data");
      }
    } catch (error) {
      console.error("❌ Quick Cost failed:", error);
      // Extract error message with fallbacks
      let errorMessage = error?.message || error?.error || (typeof error === 'string' ? error : 'Unknown error occurred');
      
      // Replace newlines with spaces for alert display (alerts don't support newlines well)
      // But keep the structure readable
      const alertMessage = errorMessage.replace(/\n\n/g, '\n').replace(/\n/g, ' ');
      
      console.error("   Error details:", {
        message: errorMessage,
        error: error,
        stack: error?.stack
      });
      
      // Show alert with the error message
      alert(`Failed to calculate cost:\n\n${errorMessage}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const buildLaminatesStreamingRequest = (sourceProductData, existingPlaybook) => {
    const layers = sourceProductData?.partDetails?.layers || [];
    if (layers.length === 0) {
      throw new Error("At least one layer is required. Please add layers in Part Details.");
    }

    const validationErrors = [];
    const normalizedLayers = [];

    layers.forEach((layer, index) => {
      const microns = layer.microns;
      const micronsNum =
        typeof microns === "number"
          ? microns
          : (typeof microns === "string" && microns.trim() !== "" ? parseFloat(microns) : 0);

      if (microns === "" || microns === null || microns === undefined || isNaN(micronsNum) || micronsNum < 0) {
        const layerName = layer.layerName || `Layer ${index + 1}`;
        const currentValue =
          microns === "" || microns === null || microns === undefined
            ? "empty"
            : (isNaN(micronsNum) ? `invalid (${microns})` : `negative (${micronsNum})`);
        validationErrors.push(`${layerName}: Microns value is ${currentValue}`);
      } else {
        normalizedLayers.push({
          ...layer,
          microns: micronsNum,
        });
      }
    });

    if (validationErrors.length > 0) {
      throw new Error(
        `Please fix the following layer issues before running Quick Cost:\n\n${validationErrors.map((err, idx) => `${idx + 1}. ${err}`).join("\n")}\n\nAll layers must have a valid Microns value (0 or greater).`
      );
    }

    const dims = sourceProductData?.partWeightDimensions || {};
    const isPouchBasis = (dims.measurementBasis || "repeat") === "pouch";

    return {
      productData: {
        ...sourceProductData,
        partWeightDimensions: {
          ...dims,
          partLength: isPouchBasis ? (dims.pouchLength || "") : (dims.partLength || ""),
          partWidth: isPouchBasis ? (dims.pouchWidth || "") : (dims.partWidth || ""),
        },
        partDetails: {
          ...sourceProductData.partDetails,
          layers: normalizedLayers,
        },
      },
      projectId: `PROJ_${Date.now()}`,
      partKey: "P1",
      ...(existingPlaybook && { existingPlaybook: existingPlaybook }),
    };
  };

  const handleLaminatesStreamingComplete = async (costResult) => {
    if (!costResult || costResult.status !== "success" || !costResult.ShouldCostResult) {
      throw new Error(costResult?.message || "Laminates analysis returned no data");
    }

    const shouldCost = costResult.ShouldCostResult;
    const material = Number(shouldCost.MaterialCost || 0);
    const process = Number(shouldCost.ProcessCost || 0);
    const ohp = Number(shouldCost.OHPCost || 0);
    const total = Number(shouldCost.TotalCost || material + process + ohp);
    const batchSize = Number(productData?.batchSize || 0);
    const dims = productData?.partWeightDimensions || {};
    const partLength = Number(dims.partLength || dims.pouchLength || 0);
    const partWidth = Number(dims.partWidth || dims.pouchWidth || 0);
    const baseCost = material + process;
    const ohpRate = baseCost > 0 ? Number(((ohp / baseCost) * 100).toFixed(2)) : 0;

    const assumptions = [
      productData?.batchSize ? `Batch size: ${productData.batchSize}` : null,
      Number.isFinite(batchSize) && batchSize > 0 ? `Total batch cost: $${(total * batchSize).toFixed(4)}` : null,
      `Total cost per unit: $${total.toFixed(4)}`,
      productData?.manufacturingCountry ? `Manufacturing Country: ${productData.manufacturingCountry}` : null,
    ].filter(Boolean);

    setCostData({
      summary: `Laminates cost: $${total.toFixed(4)} per unit`,
      breakdown: {
        material,
        process,
        ohp,
        total_should_cost: total,
      },
      assumptions,
      meta: {
        ...(costResult.summary || {}),
      },
      _source: "laminates_streaming",
      _originalResult: costResult,
    });

    setPlayBookData((prev) => ({
      ...prev,
      costParams: {
        ...(prev?.costParams || {}),
        blankLengthCm: prev?.costParams?.blankLengthCm || partLength / 10,
        blankWidthCm: prev?.costParams?.blankWidthCm || partWidth / 10,
        areaNetM2: prev?.costParams?.areaNetM2 || ((partLength * partWidth) / 1000000),
        wastagePercent: prev?.costParams?.wastagePercent || 0,
        ohpRate: prev?.costParams?.ohpRate || ohpRate,
      },
    }));

    if (costResult.riskData) {
      const unwrappedRiskData = costResult.riskData.riskData || costResult.riskData;
      setRiskData(unwrappedRiskData);
    }

    if (costResult.substituteData) {
      const unwrappedSubstituteData = costResult.substituteData.substituteData || costResult.substituteData;
      setSubstituteData(unwrappedSubstituteData);
    }

    if (!costResult.riskData || !costResult.substituteData) {
      await fetchRiskAndSubstituteData(
        productData,
        playBookData || {},
        {
          material,
          process,
          ohp,
          total_should_cost: total,
        }
      );
    }

    setActiveSection("results");
    setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));
    generateProductImageWithData(productData, playBookData || {});
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis for Laminates with SSE...");

    const initialSteps = Array.from({ length: 2 }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Processing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setKeyInsightsData(null);
    setRiskData(null);
    setSubstituteData(null);

    let maxProgress = 0;

    try {
      const requestData = buildLaminatesStreamingRequest(productData, playBookData);
      const response = await apiFetch("/api/packaging/laminates/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.trim()) continue;

          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          const eventData = JSON.parse(dataMatch[1]);
          console.log("Laminates SSE Event:", eventData);

          if (eventData.type === "start") {
            const totalSteps = eventData.total_steps || 2;
            setProgressSteps(
              Array.from({ length: totalSteps }, (_, i) => ({
                id: `step-${i + 1}`,
                label: `Step ${i + 1}`,
                status: "pending",
                message: "",
              }))
            );
          } else if (eventData.type === "progress") {
            const stepIndex = eventData.step - 1;
            setProgressSteps((prev) => {
              const updated = [...prev];
              if (updated[stepIndex]) {
                updated[stepIndex] = {
                  ...updated[stepIndex],
                  label: eventData.label || updated[stepIndex].label,
                  status:
                    eventData.status === "running"
                      ? "in-progress"
                      : (eventData.status === "success" ? "completed" : eventData.status),
                  message:
                    eventData.message ||
                    (eventData.status === "running" ? "Processing..." : "Complete"),
                  error: eventData.error,
                };
              }
              return updated;
            });

            if (eventData.status === "running" || eventData.status === "success") {
              setCurrentProgressStep(eventData.step);
            }

            const newProgress = (eventData.step / eventData.total) * 100;
            if (newProgress > maxProgress) {
              maxProgress = newProgress;
              setOverallProgress(newProgress);
            }
          } else if (eventData.type === "complete") {
            maxProgress = 100;
            setOverallProgress(100);
            setProgressSteps((prev) =>
              prev.map((step) => ({ ...step, status: "completed", message: "Complete" }))
            );

            await handleLaminatesStreamingComplete(eventData);

            setShowProgressTracker(false);
            setIsProcessing(false);
            return;
          } else if (eventData.type === "error") {
            throw new Error(eventData.message || "Analysis failed");
          }
        }
      }

      throw new Error("Stream ended without a completion event");
    } catch (error) {
      console.error("Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  const handleQuickCost = async () => {
    // Use progressive tracker for better UX
    return handleProgressiveQuickCost();
  };

  // Render assumptions function to display product information and assumptions
  const renderAssumptions = ({
    assumptions = [],
    productData,
    formulaResult,
    perlColors,
    perlTypography,
  }) => {
    return (
      <Box>
        {assumptions && assumptions.length > 0 && (
          <Paper
            sx={{
              p: 3,
              bgcolor: perlColors.surface,
              border: `1px solid ${perlColors.border}`,
              borderRadius: 2,
              mt: 3,
            }}
          >
            {(() => {
              const bottomSectionKeywords = [
                "Batch size:",
                "Total batch cost:",
                "Total cost per unit:",
                "Total cost per tonne:",
              ];

              // Case-insensitive matching for keywords
              const topAssumptions = assumptions.filter((assumption) => {
                const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                return !bottomSectionKeywords.some((keyword) =>
                  assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
                );
              });
              const bottomAssumptions = assumptions.filter((assumption) => {
                const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                return bottomSectionKeywords.some((keyword) =>
                  assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
                );
              });

              // Build product information from productData if available
              // Only show selected values from productData, not assumptions
              const buildProductInfo = () => {
                if (!productData) {
                  return [];
                }

                const productInfo = [];

                // Product Name
                if (productData.productName) {
                  productInfo.push(`Product Name: ${productData.productName}`);
                }

                // Batch Size
                if (productData.batchSize) {
                  productInfo.push(`Batch Size: ${productData.batchSize}`);
                }

                // Dimensions
                const dims = productData.partWeightDimensions;
                if (dims) {
                  const basis = dims.measurementBasis || "repeat";
                  if (basis === "pouch") {
                    const dimParts = [];
                    if (dims.pouchLength) dimParts.push(`Pouch L: ${dims.pouchLength}mm`);
                    if (dims.pouchWidth) dimParts.push(`Pouch W: ${dims.pouchWidth}mm`);
                    if (dims.sealAllowance !== undefined && dims.sealAllowance !== null && dims.sealAllowance !== "") {
                      dimParts.push(`Seal allowance: ${dims.sealAllowance}mm`);
                    }
                    if (dims.gusset !== undefined && dims.gusset !== null && dims.gusset !== "") {
                      dimParts.push(`Gusset: ${dims.gusset}mm`);
                    }
                    if (dimParts.length > 0) {
                      productInfo.push(`Dimensions: Basis: Pouch/Sachet, ${dimParts.join(", ")}`);
                    }
                  } else {
                    const dimParts = [];
                    if (dims.partLength) dimParts.push(`Repeat: ${dims.partLength}mm`);
                    if (dims.partWidth) dimParts.push(`Coil W: ${dims.partWidth}mm`);
                    if (dims.wastagePct !== undefined && dims.wastagePct !== null && dims.wastagePct !== "") {
                      dimParts.push(`Wastage: ${dims.wastagePct}%`);
                    }
                    if (dimParts.length > 0) {
                      productInfo.push(`Dimensions: Basis: Repeat x Coil width, ${dimParts.join(", ")}`);
                    }
                  }
                }

                // Layers (if available)
                const partDetails = productData.partDetails || {};
                if (partDetails.layers && Array.isArray(partDetails.layers) && partDetails.layers.length > 0) {
                  const layersInfo = partDetails.layers
                    .map((layer, idx) => {
                      if (layer.layerName && layer.filmStructure) {
                        const microns = layer.microns !== undefined && layer.microns !== null ? layer.microns : "";
                        const type = layer.type ? `, ${layer.type}` : "";
                        return `${layer.layerName}: ${layer.filmStructure}${type}${microns !== "" ? ` (${microns} microns)` : ""}`;
                      }
                      return null;
                    })
                    .filter(Boolean);
                  if (layersInfo.length > 0) {
                    productInfo.push(`Layers: ${layersInfo.join(", ")}`);
                  }
                }

                // Part Details (Regrind if available)
                if (partDetails.regrindPct) {
                  productInfo.push(`Part Details: Regrind: ${partDetails.regrindPct}%`);
                }

                // Manufacturing Country
                if (productData.manufacturingCountry) {
                  productInfo.push(
                    `Manufacturing Country: ${productData.manufacturingCountry}`
                  );
                }

                return productInfo;
              };

              const productInfoItems = buildProductInfo();

              return (
                <Box sx={{ display: "flex", gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "18px",
                        fontFamily: perlTypography?.family || "inherit",
                        letterSpacing: "0.3px",
                        color: perlColors.textPrimary,
                      }}
                    >
                      Product Information
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {productInfoItems.map((item, idx) => {
                        const itemStr = typeof item === 'string' 
                          ? item 
                          : (typeof item === 'object' && item !== null 
                              ? (item.value || item.name || item.reason || JSON.stringify(item))
                              : String(item || ''));
                        return (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontSize: "14px",
                              fontFamily: perlTypography?.family || "inherit",
                              color: perlColors.textSecondary,
                              lineHeight: "20px",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {itemStr}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>

                  {bottomAssumptions.length > 0 && (
                    <Box
                      sx={{
                        width: "1px",
                        bgcolor: perlColors.border || "rgba(255, 255, 255, 0.12)",
                        alignSelf: "stretch",
                      }}
                    />
                  )}

                  {bottomAssumptions.length > 0 && (
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          fontSize: "14px",
                          fontWeight: 600,
                          lineHeight: "18px",
                          fontFamily: perlTypography?.family || "inherit",
                          letterSpacing: "0.3px",
                          color: perlColors.textPrimary,
                        }}
                      >
                        Assumptions
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {bottomAssumptions.map((assumption, idx) => {
                          const assumptionStr = typeof assumption === 'string' 
                            ? assumption 
                            : (typeof assumption === 'object' && assumption !== null 
                                ? (assumption.value || assumption.name || assumption.reason || JSON.stringify(assumption))
                                : String(assumption || ''));
                          return (
                            <Typography
                              key={idx}
                              component="li"
                              variant="body2"
                              sx={{
                                mb: 1,
                                fontSize: "14px",
                                fontFamily: perlTypography?.family || "inherit",
                                color: perlColors.textSecondary,
                                lineHeight: "20px",
                                letterSpacing: "0.3px",
                              }}
                            >
                              {assumptionStr}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Paper>
        )}
      </Box>
    );
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
 
  const costTotal = useMemo(() => {
    // Calculate total from rows first
    const sumFromRows = costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
    
    // Check if declared total exists and is valid
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    
    // Use declared total only if it's a valid finite number AND greater than 0
    // If declared total is 0 or very small, prefer sum from rows
    if (Number.isFinite(declaredNumber) && declaredNumber > 0.000001) {
      return declaredNumber;
    }
    
    // Fallback to sum from rows (should always be > 0 if there are rows)
    return sumFromRows > 0 ? sumFromRows : declaredNumber;
  }, [costData, costRows]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Packaging"
          industry="Packaging"
          commodity="Laminates"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height:"92%"}}>
                <LaminatesQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  initialData={productData}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <LaminatesPlayBook data={playBookData} onChange={handlePlayBookChange} />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate recipe first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: costData?.assumptions || [],
                      productData,
                      formulaResult: costData?.formulaResult,
                      perlColors,
                      perlTypography: theme.custom?.perlTypography || {},
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button */}
        {sectionStarted.product &&  (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
